import { Distance, KeyBinder } from '../../../../FrontendBundle/Resources/js/ofcs';
import MoveItemsCommand from './command/moveItemsCommand';
import GridItem from './item/gridItem';

/**
 * Helper class for easy callback key bindings
 *
 * @class EditorKeyBinder
 */
export default class EditorKeyBinder extends KeyBinder {
    TOOL_SELECT = 'select';
    TOOL_MOVE = 'move';
    TOOL_CREATE_CIRCLE = 'createCircle';
    TOOL_CREATE_RECTANGLE = 'createRectangle';
    TOOL_CREATE_GRIP = 'createGrip';
    TOOL_TEXT = 'text';

    constructor(element, editor, clipboard) {
        super(element);

        this.editor = editor;
        this.clipboard = clipboard;
    }

    getMappings() {
        return [{
            keyCodes: [this.KEY_CODE_UP],
            callback: this.moveUp,
        }, {
            keyCodes: [this.KEY_CODE_UP],
            shift: true,
            callback: this.move10Up,
        }, {
            keyCodes: [this.KEY_CODE_RIGHT],
            callback: this.moveRight,
        }, {
            keyCodes: [this.KEY_CODE_RIGHT],
            shift: true,
            callback: this.move10Right,
        }, {
            keyCodes: [this.KEY_CODE_DOWN],
            callback: this.moveDown,
        }, {
            keyCodes: [this.KEY_CODE_DOWN],
            shift: true,
            callback: this.move10Down,
        }, {
            keyCodes: [this.KEY_CODE_LEFT],
            callback: this.moveLeft,
        }, {
            keyCodes: [this.KEY_CODE_LEFT],
            shift: true,
            callback: this.move10Left,
        }, {
            keyCodes: [this.KEY_CODE_DEL],
            callback: this.deleteSelection,
        }, {
            keyCodes: [this.KEY_CODE_BACKSPACE],
            callback: this.deleteSelection,
        }, {
            keyCodes: [this.KEY_CODE_C],
            meta: true,
            callback: this.copySelection,
        }, {
            keyCodes: [this.KEY_CODE_X],
            meta: true,
            callback: this.cutSelection,
        }, {
            keyCodes: [this.KEY_CODE_V],
            meta: true,
            callback: this.pasteSelection,
        }, {
            keyCodes: [this.KEY_CODE_SPACE],
            callback: this.activateTempMoveTool,
        }, {
            keyCodes: [this.KEY_CODE_SPACE],
            event: 'keyup',
            callback: this.deactivateTempMoveTool,
        }, {
            keyCodes: [this.KEY_CODE_S],
            meta: true,
            callback: this.save,
        }, {
            keyCodes: [this.KEY_CODE_S],
            callback: this.activateSelectTool,
        }, {
            keyCodes: [this.KEY_CODE_M],
            callback: this.activateMoveTool,
        }, {
            keyCodes: [this.KEY_CODE_C],
            callback: this.activateCircleTool,
        }, {
            keyCodes: [this.KEY_CODE_R],
            callback: this.activateRectangleTool,
        }, {
            keyCodes: [this.KEY_CODE_G],
            callback: this.activateGripTool,
        }, {
            keyCodes: [this.KEY_CODE_M],
            meta: true,
            callback: this.toggleGrid,
        }, {
            keyCodes: [this.KEY_CODE_H],
            meta: true,
            callback: this.toggleGuides,
        }, {
            keyCodes: [this.KEY_CODE_I],
            meta: true,
            callback: this.toggleImages,
        }, {
            keyCodes: [this.KEY_CODE_4, this.KEY_CODE_NUM_4],
            meta: true,
            callback: this.alignLeft,
        }, {
            keyCodes: [this.KEY_CODE_5, this.KEY_CODE_NUM_5],
            meta: true,
            callback: this.alignCenter,
        }, {
            keyCodes: [this.KEY_CODE_6, this.KEY_CODE_NUM_6],
            meta: true,
            callback: this.alignRight,
        }, {
            keyCodes: [this.KEY_CODE_6, this.KEY_CODE_NUM_6, this.KEY_CODE_CLOSE_BRACKET],
            meta: true,
            alt: true,
            callback: this.alignTop,
        }, {
            keyCodes: [this.KEY_CODE_5, this.KEY_CODE_NUM_5, this.KEY_CODE_OPEN_BRACKET],
            meta: true,
            alt: true,
            callback: this.alignMiddle,
        }, {
            keyCodes: [this.KEY_CODE_4, this.KEY_CODE_NUM_4],
            meta: true,
            alt: true,
            callback: this.alignBottom,
        }, {
            keyCodes: [this.KEY_CODE_G],
            meta: true,
            callback: this.toggleGroups,
        }, {
            keyCodes: [this.KEY_CODE_K],
            meta: true,
            callback: this.toggleCombine,
        }, {
            keyCodes: [this.KEY_CODE_T],
            callback: this.activateTextTool,
        }, {
            keyCodes: [this.KEY_CODE_F],
            meta: true,
            callback: this.toggleFlip,
        }, {
            keyCodes: [this.KEY_CODE_Z],
            meta: true,
            callback: this.undo,
        }, {
            keyCodes: [this.KEY_CODE_Z],
            shift: true,
            meta: true,
            callback: this.redo,
        }, {
            keyCodes: [this.KEY_CODE_PLUS, this.KEY_CODE_PLUS_NUMPAD, this.KEY_CODE_PLUS_FIREFOX],
            meta: true,
            callback: this.zoomIn,
        }, {
            keyCodes: [this.KEY_CODE_PLUS, this.KEY_CODE_PLUS_NUMPAD, this.KEY_CODE_PLUS_FIREFOX],
            meta: true,
            alt: true,
            callback: this.zoomIn,
        }, {
            keyCodes: [this.KEY_CODE_MINUS, this.KEY_CODE_MINUS_NUMPAD, this.KEY_CODE_MINUS_FIREFOX],
            meta: true,
            callback: this.zoomOut,
        }, {
            keyCodes: [this.KEY_CODE_MINUS, this.KEY_CODE_MINUS_NUMPAD, this.KEY_CODE_MINUS_FIREFOX],
            meta: true,
            alt: true,
            callback: this.zoomOut,
        }, {
            keyCodes: [this.KEY_CODE_0, this.KEY_CODE_NUM_0],
            meta: true,
            callback: this.zoomFit,
        }, {
            keyCodes: [this.KEY_CODE_1, this.KEY_CODE_NUM_1],
            meta: true,
            alt: true,
            callback: this.spreadCenterHorizontal,
        }, {
            keyCodes: [this.KEY_CODE_2, this.KEY_CODE_NUM_2],
            meta: true,
            alt: true,
            callback: this.spreadCenterVertical,
        }, {
            keyCodes: [this.KEY_CODE_1, this.KEY_CODE_NUM_1],
            meta: true,
            callback: this.spreadMarginHorizontal,
        }, {
            keyCodes: [this.KEY_CODE_2, this.KEY_CODE_NUM_2],
            meta: true,
            callback: this.spreadMarginVertical,
        }, {
            keyCodes: [this.KEY_CODE_ESC],
            callback: this.triggerAbort,
        }];
    }

    /**
     * Move selection up
     */
    moveUp() {
        this.move(new Distance(0, -1));
    }

    /**
     * Move selection 10 pixel up
     */
    move10Up() {
        this.move(new Distance(0, -10));
    }

    /**
     * Move selection right
     */
    moveRight() {
        this.move(new Distance(1, 0));
    }

    /**
     * Move selection 10 pixel right
     */
    move10Right() {
        this.move(new Distance(10, 0));
    }

    /**
     * Move selection down
     */
    moveDown() {
        this.move(new Distance(0, 1));
    }

    /**
     * Move selection 10 pixel down
     */
    move10Down() {
        this.move(new Distance(0, 10));
    }

    /**
     * Move selection left
     */
    moveLeft() {
        this.move(new Distance(-1, 0));
    }

    /**
     * Move selection 10 pixel left
     */
    move10Left() {
        this.move(new Distance(-10, 0));
    }

    /**
     * Move by distance
     *
     * @param {Distance} distance
     */
    move(distance) {
        const canvas = this.editor.getCanvas();
        const history = canvas.getHistory();
        const command = new MoveItemsCommand(canvas.getSelection().toArray(), distance);

        // TODO: Move items correctly and as expected in a grid context
        if (canvas.getCurrentContext().getItem() instanceof GridItem) return;

        history.addAndExecuteCommand(command);
    }

    /**
     * Delete selection
     */
    deleteSelection() {
        this.element.find('[data-editor-tools="delete"]').trigger('click');
    }

    /**
     * Copy selection
     */
    copySelection() {
        this.element.find('[data-editor-tools="copy"]').trigger('click');
    }

    /**
     * Cut selection
     */
    cutSelection() {
        this.element.find('[data-editor-tools="cut"]').trigger('click');
    }

    /**
     * Paste selection
     */
    pasteSelection() {
        this.element.find('[data-editor-tools="paste"]').trigger('click');
    }

    /**
     * Activate temporary move tool
     */
    activateTempMoveTool() {
        if (!this.lastActiveTool) {
            this.lastActiveTool = this.editor.getCanvas().getActiveTool();
            this.editor.getCanvas().setActiveTool(this.TOOL_MOVE);
        }
    }

    /**
     * Deactivate temporary move tool
     */
    deactivateTempMoveTool() {
        if (this.lastActiveTool) {
            this.editor.getCanvas().setActiveTool(this.lastActiveTool);
            this.lastActiveTool = null;
        }
    }

    /**
     * Save
     */
    save() {
        this.element.find('[data-editor-tools="save"]').trigger('click');
    }

    /**
     * Activate select tool
     */
    activateSelectTool() {
        this.element.find('[data-editor-mode="select"]').trigger('click');
    }

    /**
     * Activate move tool
     */
    activateMoveTool() {
        this.element.find('[data-editor-mode="move"]').trigger('click');
    }

    /**
     * Activate rectangle tool
     */
    activateRectangleTool() {
        this.element.find('[data-editor-mode="createRectangle"]').trigger('click');
    }

    /**
     * Activate circle tool
     */
    activateCircleTool() {
        this.element.find('[data-editor-mode="createCircle"]').trigger('click');
    }

    /**
     * Activate grip tool
     */
    activateGripTool() {
        this.element.find('[data-editor-mode="createGrip"]').trigger('click');
    }

    /**
     * Activate text tool
     */
    activateTextTool() {
        this.element.find('[data-editor-mode="text"]').trigger('click');
    }

    /**
     * Toggles images
     */
    toggleImages() {
        this.element.find('[data-editor-toggle="images"]').trigger('click');
    }

    /**
     * Toggles guides
     */
    toggleGuides() {
        this.element.find('[data-editor-toggle="guides"]').trigger('click');
    }

    /**
     * Toggles grid
     */
    toggleGrid() {
        this.element.find('[data-editor-toggle="grid"]').trigger('click');
    }

    /**
     * Toggles item groups
     */
    toggleGroups() {
        this.element.find('[data-editor-toggle="group-items"]').trigger('click');
    }

    /**
     * Toggles combined item groups
     */
    toggleCombine() {
        this.element.find('[data-editor-toggle="combine-items"]').trigger('click');
    }

    /**
     * Toggles item flip
     */
    toggleFlip() {
        this.element.find('[data-editor-toggle="flip"]').trigger('click');
    }

    /**
     * Undo
     */
    undo() {
        this.element.find('[data-editor-history="undo"]').trigger('click');
    }

    /**
     * Redo
     */
    redo() {
        this.element.find('[data-editor-history="redo"]').trigger('click');
    }

    zoomIn() {
        this.editor.getCanvas().zoomIn();
    }

    zoomOut() {
        this.editor.getCanvas().zoomOut();
    }

    zoomFit() {
        this.editor.getCanvas().fitToCanvas();
    }

    spreadCenterHorizontal() {
        this.element.find('[data-spread="center"][data-direction="horizontal"]').trigger('click');
    }

    spreadCenterVertical() {
        this.element.find('[data-spread="center"][data-direction="vertical"]').trigger('click');
    }

    spreadMarginHorizontal() {
        this.element.find('[data-spread="margin"][data-direction="horizontal"]').trigger('click');
    }

    spreadMarginVertical() {
        this.element.find('[data-spread="margin"][data-direction="vertical"]').trigger('click');
    }

    alignLeft() {
        this.element.find('[data-align="left"]').trigger('click');
    }

    alignCenter() {
        this.element.find('[data-align="center"]').trigger('click');
    }

    alignRight() {
        this.element.find('[data-align="right"]').trigger('click');
    }

    alignTop() {
        this.element.find('[data-align="top"]').trigger('click');
    }

    alignMiddle() {
        this.element.find('[data-align="middle"]').trigger('click');
    }

    alignBottom() {
        this.element.find('[data-align="bottom"]').trigger('click');
    }

    triggerAbort() {
        this.editor.getCanvas().trigger('abortAction');
    }
}
